import type { Metadata } from "next"
import Link from "next/link"
import { redirect } from "next/navigation"

import { Container } from "@/components/layout/container"
import { SignOutButton } from "@/components/auth/sign-out-button"
import { StatusBadge } from "@/components/shared/status-badge"
import { EmptyState } from "@/components/shared/empty-state"
import { getCurrentUser } from "@/lib/auth/get-user"
import { getProfileByUserId } from "@/lib/auth/get-profile"
import { getUserDashboardData } from "@/lib/dashboard"
import { getRecentlyViewedRecipeIds } from "@/lib/recently-viewed"
import { getAllRecipes } from "@/data/recipes"
import { formatDate } from "@/lib/utils"
import { siteConfig } from "@/data/site"

export const metadata: Metadata = {
  title: `Особистий кабінет — ${siteConfig.name}`,
  description:
    "Особистий кабінет порталу Кухлик: збережені рецепти, історія переглядів, відгуки та надіслані матеріали.",
  robots: {
    index: false,
    follow: false,
  },
}

export default async function DashboardPage() {
  const user = await getCurrentUser()

  if (!user) {
    redirect("/auth/sign-in")
  }

  const [profile, dashboardData, recentlyViewedIds] = await Promise.all([
    getProfileByUserId(user.id),
    getUserDashboardData(user.id),
    getRecentlyViewedRecipeIds(),
  ])

  const allRecipes = getAllRecipes()

  const recentlyViewedRecipes = recentlyViewedIds
    .map((recipeId) => allRecipes.find((recipe) => recipe.id === recipeId))
    .filter(Boolean)
    .slice(0, 8)

  const savedCount = dashboardData.savedRecipes.length
  const submissionsCount = dashboardData.submissions.length
  const reviewsCount = dashboardData.reviews.length
  const commentsCount = dashboardData.comments.length

  return (
    <>
      {/* HERO */}
      <section className="border-b border-neutral-200 bg-gradient-to-b from-emerald-50/60 via-white to-white py-12 sm:py-16">
        <Container className="max-w-6xl">
          <div className="eyebrow">Особистий кабінет</div>

          <h1 className="mt-4 text-4xl font-semibold leading-tight text-neutral-950 sm:text-5xl">
            Ваш простір на порталі {siteConfig.name}
          </h1>

          <p className="mt-5 max-w-3xl text-base leading-8 text-neutral-700 sm:text-lg">
            Тут зібрано все важливе: ваші збереження, історія переглядів,
            відгуки та надіслані рецепти.
          </p>

          <p className="mt-4 text-sm text-neutral-600">
            Ви увійшли як{" "}
            <span className="font-medium text-neutral-950">{user.email}</span>
          </p>
        </Container>
      </section>

      <section className="py-12 sm:py-14">
        <Container className="max-w-6xl">
          {/* SUMMARY */}
          <div className="mb-8 rounded-[2rem] border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-neutral-950">
                  Огляд активності
                </h2>

                <p className="mt-3 text-sm text-neutral-600">
                  Швидкий доступ до ваших дій на сайті.
                </p>

                <p className="mt-4 text-sm text-neutral-600">
                  Роль:{" "}
                  <span className="font-medium text-neutral-950">
                    {profile?.role ?? "user"}
                  </span>
                </p>

                {profile?.role === "admin" && (
                  <div className="mt-5">
                    <Link href="/admin" className="button-secondary">
                      Панель адміністратора
                    </Link>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3 sm:items-end">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    { value: savedCount, label: "Збережено" },
                    { value: submissionsCount, label: "Надіслано" },
                    { value: reviewsCount, label: "Відгуків" },
                    { value: commentsCount, label: "Коментарів" },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-center"
                    >
                      <div className="text-2xl font-semibold text-neutral-950">
                        {item.value}
                      </div>
                      <div className="mt-1 text-xs text-neutral-500">
                        {item.label}
                      </div>
                    </div>
                  ))}
                </div>

                <SignOutButton />
              </div>
            </div>
          </div>

          {/* ACTION BLOCK (ВАЖНО!) */}
          <div className="mb-8 rounded-[2rem] border border-neutral-200 bg-gradient-to-br from-white to-emerald-50/50 p-6 shadow-sm sm:p-8">
            <h2 className="text-xl font-semibold text-neutral-950">
              Що можна зробити далі
            </h2>

            <div className="mt-4 flex flex-wrap gap-3">
              <Link href="/recipes" className="button-secondary">
                Переглянути рецепти
              </Link>

              <Link href="/submit-recipe" className="button-primary">
                Надіслати рецепт
              </Link>

              <Link href="/collections" className="button-secondary">
                Переглянути добірки
              </Link>
            </div>
          </div>

          {/* CONTENT */}
          <div className="grid gap-6">
            {/* RECENT */}
            <section className="block-section">
              <h2 className="section-title">Нещодавно переглянуті</h2>

              {recentlyViewedRecipes.length > 0 ? (
                <div className="space-y-4 mt-6">
                  {recentlyViewedRecipes.map((recipe) => (
                    <article key={recipe!.id} className="card-item">
                      <Link
                        href={`/recipes/${recipe!.slug}`}
                        className="font-semibold text-neutral-950 hover:text-emerald-700"
                      >
                        {recipe!.title}
                      </Link>

                      <p className="mt-2 text-sm text-neutral-600">
                        {recipe!.excerpt}
                      </p>
                    </article>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="🕘"
                  title="Ще немає переглядів"
                  description="Коли ви відкриєте рецепти, вони з’являться тут."
                  actionLabel="До рецептів"
                  actionHref="/recipes"
                />
              )}
            </section>

            {/* SAVED */}
            <section className="block-section">
              <h2 className="section-title">Збережені рецепти</h2>

              {dashboardData.savedRecipes.length > 0 ? (
                <div className="space-y-4 mt-6">
                  {dashboardData.savedRecipes.map((item) => (
                    <article key={item.id} className="card-item">
                      <div className="text-xs text-neutral-500">
                        {formatDate(item.savedAt)}
                      </div>

                      <Link
                        href={`/recipes/${item.slug}`}
                        className="mt-2 block font-semibold text-neutral-950 hover:text-emerald-700"
                      >
                        {item.title}
                      </Link>

                      <p className="mt-2 text-sm text-neutral-600">
                        {item.description}
                      </p>
                    </article>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="🔖"
                  title="Немає збережених рецептів"
                  description="Зберігайте рецепти, щоб швидко до них повертатися."
                  actionLabel="До рецептів"
                  actionHref="/recipes"
                />
              )}
            </section>

            {/* SUBMISSIONS */}
            <section className="block-section">
              <h2 className="section-title">Ваші рецепти</h2>

              {dashboardData.submissions.length > 0 ? (
                <div className="space-y-4 mt-6">
                  {dashboardData.submissions.map((item) => (
                    <article key={item.id} className="card-item">
                      <div className="flex items-center gap-2">
                        <StatusBadge label={item.status} />
                        <span className="text-xs text-neutral-500">
                          {formatDate(item.createdAt)}
                        </span>
                      </div>

                      <div className="mt-2 font-semibold text-neutral-950">
                        {item.title}
                      </div>

                      <p className="mt-2 text-sm text-neutral-600">
                        {item.description}
                      </p>
                    </article>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="📬"
                  title="Ви ще не надсилали рецептів"
                  description="Поділіться своїм рецептом і він з’явиться тут."
                  actionLabel="Надіслати рецепт"
                  actionHref="/submit-recipe"
                />
              )}
            </section>
          </div>
        </Container>
      </section>
    </>
  )
}